import { CommonModule } from '@angular/common';
import { Component, Input, output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import { TranslocoModule } from '@jsverse/transloco';
import { ConversePromptOverrideModalComponent } from '../converse-prompt-override-modal/converse-prompt-override-modal.component';

@Component({
  selector: 'simulation-prompts',
  standalone: true,
  templateUrl: './simulation-prompt.component.html',
  styleUrls: ['./simulation-prompt.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    TranslocoModule,
    ConversePromptOverrideModalComponent,
  ],
})
export class SimulationPromptComponent {
  @Input() isQcAdmin = false;
  @Input() isRefinementInProgress = false;

  readonly conversePromptOverridden = output<void>();

  form!: FormGroup;
  converseSection!: FormGroup;
  callUuid: string = '';
  modalOpen = false;

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.loadForm();

    if (!this.isSelfAuthoringFlow) {
      this.setReadOnlyMode();
    }
  }

  private setReadOnlyMode() {
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.conversePromptGroup?.get('promptText'),
    );
  }

  getReadOnlyMode() {
    return this.authorReactiveFormService.isReadOnly(
      this.callUuid,
    );
  }

  get isSelfAuthoringFlow(): boolean {
    return this.isQcAdmin || this.authorReactiveFormService.isSelfAuthoring;
  }

  get conversePromptGroup(): FormGroup | undefined {
    return this.converseSection ?? undefined;
  }

  get currentPromptText(): string {
    return this.conversePromptGroup?.get('promptText')?.value ?? '';
  }

  openModal(): void {
    this.modalOpen = true;
  }

  onModalClose(): void {
    this.modalOpen = false;
  }

  onOverrideSuccess(result: { promptUuid: string; promptText: string }): void {
    this.modalOpen = false;
    this.authorReactiveFormService.patchForm(
      this.callUuid,
      SimulationConstants.CONVERSE_PROMPT_PATH,
      { uuid: result.promptUuid, promptText: result.promptText },
    );
    this.form.updateValueAndValidity();
    this.conversePromptOverridden.emit();
  }

  private loadForm(): void {
    this.authorReactiveFormService.getForm(this.callUuid).subscribe((form) => {
      this.form = form.get(SimulationConstants.CONTENT_SECTION) as FormGroup;
      this.converseSection = form.get(
        SimulationConstants.CONVERSE_PROMPT_PATH,
      ) as FormGroup;

      if (!this.isSelfAuthoringFlow) {
        this.setupAutoSave(
          this.converseSection,
          SimulationConstants.CONVERSE_PROMPT_PATH,
        );
      }
    });
  }

  private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        const readOnly = this.authorReactiveFormService.isReadOnly(
          this.callUuid,
        );

        this.authorReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue,
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string,
  ): void {
    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authorReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
      });

      this.form.updateValueAndValidity();
    });
  }
}
